import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Save } from 'lucide-react'
import { toast } from 'sonner'
import { portfoliosService, PortfolioUpdate, Portfolio } from '../services/portfolios'
import { getErrorMessage } from '../utils/apiTransformers'
import type { ApiErrorLike } from '../utils/apiError'

export default function EditPortfolio() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(true)
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null)
  const [formData, setFormData] = useState<PortfolioUpdate>({
    name: '',
    description: '',
    status: 'planning',
    start_date: '',
    end_date: '',
  })

  useEffect(() => {
    if (id) {
      fetchPortfolio()
    }
  }, [id])

  const fetchPortfolio = async () => {
    if (!id) {
      navigate('/portfolio')
      return
    }
    const portfolioId = parseInt(id)
    if (isNaN(portfolioId)) {
      toast.error('Invalid portfolio ID')
      navigate('/portfolio')
      return
    }
    setFetching(true)
    try {
      const data = await portfoliosService.get(portfolioId)
      setPortfolio(data)
      setFormData({
        name: data.name,
        description: data.description || '',
        status: data.status,
        start_date: data.start_date || '',
        end_date: data.end_date || '',
      })
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error fetching portfolio:', error)
      toast.error(getErrorMessage(error) || 'Failed to load portfolio')
      navigate('/portfolio')
    } finally {
      setFetching(false)
    }
  }

  const handleChange = (field: keyof PortfolioUpdate) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const value = e.target.value
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (!formData.name?.trim()) {
        toast.error('Portfolio name is required')
        setLoading(false)
        return
      }

      if (formData.start_date && formData.end_date) {
        if (new Date(formData.end_date) < new Date(formData.start_date)) {
          toast.error('End date must be after start date')
          setLoading(false)
          return
        }
      }

      const portfolioId = parseInt(id!)
      if (isNaN(portfolioId)) {
        toast.error('Invalid portfolio ID')
        return
      }
      await portfoliosService.update(portfolioId, formData)
      toast.success('Portfolio updated successfully!')
      navigate(`/portfolio/${id}`)
    } catch (errorRaw) {
      const error = errorRaw as ApiErrorLike
      console.error('Error updating portfolio:', error)
      toast.error(getErrorMessage(error) || 'Failed to update portfolio. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (fetching) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f0f4f8]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#486581] mx-auto mb-4"></div>
          <p className="text-[#486581]">Loading portfolio...</p>
        </div>
      </div>
    )
  }

  if (!portfolio) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#f0f4f8]">
        <div className="text-center">
          <p className="text-[#102a43]">Portfolio not found</p>
          <button
            onClick={() => navigate('/portfolio')}
            className="mt-4 px-4 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692]"
          >
            Back to Portfolios
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6 bg-[#f0f4f8] min-h-screen">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/portfolio/${id}`)}
            className="px-4 py-2 bg-[#486581] hover:bg-[#5a7692] border border-[#829ab1] rounded-lg transition-colors flex items-center gap-2 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Portfolio
          </button>
          <h1 className="text-3xl font-semibold text-[#2A3B4C] mb-2">Edit Portfolio</h1>
          <p className="text-lg text-[#556B7C]">Update portfolio details</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-xl border-2 border-[#bcccdc] shadow-md p-6 space-y-6">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-[#102a43] mb-2">
              Portfolio Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={handleChange('name')}
              placeholder="Enter portfolio name"
              className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-[#102a43] mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={handleChange('description')}
              placeholder="Enter portfolio description"
              rows={4}
              className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none resize-none text-[#2A3B4C]"
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-[#102a43] mb-2">
              Status
            </label>
            <select
              value={formData.status}
              onChange={handleChange('status')}
              className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
            >
              <option value="planning">Planning</option>
              <option value="active">Active</option>
              <option value="on_hold">On Hold</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#102a43] mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={handleChange('start_date')}
                className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#102a43] mb-2">
                End Date
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={handleChange('end_date')}
                className="w-full px-4 py-2 border-2 border-[#bcccdc] rounded-lg focus:border-[#486581] focus:outline-none text-[#2A3B4C]"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4 border-t border-[#bcccdc]">
            <button
              type="button"
              onClick={() => navigate(`/portfolio/${id}`)}
              className="px-6 py-2 border-2 border-[#bcccdc] text-[#486581] rounded-lg hover:bg-[#f0f4f8] transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.name?.trim()}
              className="px-6 py-2 bg-[#486581] text-white rounded-lg hover:bg-[#5a7692] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Update Portfolio
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

